import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { useAuth, dashboardPathForRole } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect to their dashboard
  React.useEffect(() => {
    if (user && user.role) {
      const target = location.state?.from || dashboardPathForRole(user.role);
      navigate(target, { replace: true });
    }
  }, [user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Inserisci email e password.");
      return;
    }
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error || "Credenziali non valide.");
      return;
    }
    navigate(dashboardPathForRole(res.user.role), { replace: true });
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-brand-bg brand-grid-bg flex flex-col"
    >
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Link
          to="/"
          data-testid="login-back-home"
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4"
        >
          <ArrowLeft size={16} /> Torna alla home
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pb-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 justify-center mb-8">
            <img
              src={`${process.env.PUBLIC_URL}/logo-academy-full.png`}
              alt="Buro Academy"
              className="h-8 w-auto"
            />
          </div>

          <div className="bg-white border border-brand-line rounded-sm shadow-sm p-8">
            <div className="gold-rule" />
            <h1 className="text-2xl text-brand-ink mb-1">Accedi al tuo account</h1>
            <p className="text-sm text-brand-muted mb-6">
              Inserisci le credenziali fornite dall’amministrazione.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-sm text-brand-ink">
                  Email o codice fiscale
                </Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="text"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email oppure codice fiscale"
                  className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue"
                  required
                />
              </div>

              <div>
                <div className="flex items-baseline justify-between gap-3">
                  <Label htmlFor="password" className="text-sm text-brand-ink">
                    Password
                  </Label>
                  <Link
                    to="/password-dimenticata"
                    data-testid="login-forgot-password-link"
                    className="text-sm text-brand-blue hover:underline underline-offset-4"
                  >
                    Password dimenticata?
                  </Link>
                </div>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue"
                  required
                />
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  role="alert"
                  className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                >
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Accesso in corso…
                  </>
                ) : (
                  <>Accedi</>
                )}
              </button>
            </form>
          </div>

          <p className="text-sm text-center text-brand-muted mt-6">
            Non hai un account?{" "}
            <Link to="/registrati" className="text-brand-blue hover:underline underline-offset-4">
              Registrati come privato
            </Link>{" "}
            oppure{" "}
            <Link to="/registrati/azienda" className="text-brand-blue hover:underline underline-offset-4">
              registra un'azienda
            </Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
